// 导出相关工具函数，供历史页和计算页复用
import { calculateProtectionSettings } from './protectionCalculations';
import { getResultLabel, getParamLabel } from './labelMappings';
import { getFieldRange } from './validationRules';
// 重资源按需加载：在使用处动态 import，避免主包体积膨胀

// 保护类型名称映射（支持可选 i18n 翻译器）
// 用法：在 UI 环境传入 { t } 以返回当前语言；在纯工具/测试环境不传则回退中文。
export const getProtectionTypeName = (type, opts = {}) => {
  const mapZh = {
    overcurrent: '过电流保护',
    distance: '距离保护',
    differential: '差动保护',
    directional: '方向保护',
    zero_sequence: '零序保护',
    longitudinal: '纵联差动'
  };
  const t = typeof opts.t === 'function' ? opts.t : null;
  if (t) {
    const key = `protection.type.${type}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return mapZh[type] || type;
};

// 计算公式（与 CalculationPage.jsx 保持一致）
export const getCalculationFormula = (protectionType, opts = {}) => {
  const t = typeof opts.t === 'function' ? opts.t : null;
  const TT = (key, fallback) => (t ? t(key) : fallback);
  switch (protectionType) {
    case 'overcurrent':
      return {
        title: TT('export.report.formulas.overcurrent.title', '过电流保护计算公式'),
        formulas: [
          TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
          TT('export.report.formulas.overcurrent.iop', '启动电流定值: Iop = Krel × Irated / nCT'),
          TT('export.report.formulas.overcurrent.ksen', '灵敏系数: Ksen = If.min / Iop'),
          TT('export.report.formulas.overcurrent.time', '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)'),
          TT('export.report.formulas.overcurrent.notes', '其中: Krel=1.2~1.5，Irated为额定电流，nCT为CT变比；M=I/Iop；k、α为曲线常数（IEC 60255）')
        ]
      };
    case 'distance':
      return {
        title: TT('export.report.formulas.distance.title', '距离保护计算公式'),
        formulas: [
          TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
          TT('export.report.formulas.distance.zm', '测量阻抗: Zm = U / I'),
          TT('export.report.formulas.distance.zset1', '第I段定值: Zset.I = Krel × Zline'),
          TT('export.report.formulas.distance.zset2', '第II段定值: Zset.II = Zline + Krel × Znext'),
          TT('export.report.formulas.distance.zset3', '第III段定值: Zset.III = Krel × Zload.min / Kss.max'),
          TT('export.report.formulas.distance.notes', '示意：Krel为可靠系数，Zline为线路阻抗；实际需校核弧光电阻、最小运行方式与区段配合')
        ]
      };
    case 'differential':
      return {
        title: TT('export.report.formulas.differential.title', '差动保护计算公式'),
        formulas: [
          TT('export.report.formulas.differential.id', '差动电流: Id = |I1 + I2|'),
          TT('export.report.formulas.differential.ir', '制动电流: Ir = (|I1| + |I2|) / 2'),
          TT('export.report.formulas.differential.instant_trip', '差动速断: Id > Isd'),
          TT('export.report.formulas.differential.ratio_restraint', '比率制动: Id > Icd + K × (Ir - Ibr)'),
          TT('export.report.formulas.differential.harmonic_blocking', '二次谐波制动: I2/I1 > Kb'),
          TT('export.report.formulas.differential.notes', '其中: Isd为速断定值, Icd为差动定值, K为制动系数')
        ]
      };
    case 'directional':
      return {
        title: TT('export.report.formulas.directional.title', '方向保护计算公式'),
        formulas: [
          TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
          TT('export.report.formulas.directional.vsec', '二次电压: Vsec = U / nPT'),
          TT('export.report.formulas.directional.isec', '二次电流: Isec = If / nCT'),
          TT('export.report.formulas.directional.p_criterion', '相量有功分量: P = Re{V × I*} ≈ Vsec × Isec × cos(φ − θp)'),
          TT('export.report.formulas.directional.direction_criterion', '方向判据: sign(Re{V × I*})（示意）'),
          TT('export.report.formulas.directional.polarizing_angle_range', '极化角范围与建议: θp ∈ [0°, 90°]（典型 20°~60°）'),
          TT('export.report.formulas.directional.iop', '启动电流: Iop = 1.1 × Irated / nCT'),
          TT('export.report.formulas.directional.multiple', '倍数: M = Isec / Iop'),
          TT('export.report.formulas.directional.time', '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)')
        ]
      };
    case 'zero_sequence':
      return {
        title: TT('export.report.formulas.zero_sequence.title', '零序保护计算公式'),
        formulas: [
          TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
          TT('export.report.formulas.zero_sequence.i0', '二次零序电流: I0 = k0 × If / nCT'),
          TT('export.report.formulas.zero_sequence.iop0', '启动电流: Iop0 = 0.4 × Irated / nCT'),
          TT('export.report.formulas.zero_sequence.multiple', '倍数: M = I0 / Iop0'),
          TT('export.report.formulas.zero_sequence.time', '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)'),
          TT('export.report.formulas.zero_sequence.notes', '其中: k0 为近似换算系数，用于从一次零序电流估算二次值；实际取值需结合系统接地方式与装置系数校核')
        ]
      };
    case 'longitudinal':
      return {
        title: TT('export.report.formulas.longitudinal.title', '纵联差动保护计算公式'),
        formulas: [
          TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
          TT('export.report.formulas.longitudinal.isec', '二次电流: Isec = If / nCT'),
          TT('export.report.formulas.longitudinal.id', '差动电流: Id = |I本端 + I对端|（单侧供电近似 Id ≈ Isec）'),
          TT('export.report.formulas.longitudinal.iop', '启动电流: Iop = 1.2 × Irated / nCT'),
          TT('export.report.formulas.longitudinal.time', '动作时间: t = tdef（快速动作，典型 0.05s）')
        ]
      };
    default:
      return {
        title: TT('export.report.formulas.default.title', '保护计算公式'),
        formulas: [TT('export.report.formulas.default.placeholder', '请根据保护类型查看对应计算公式')]
      };
  }
};

// 计算公式评估：根据输入参数与已计算结果，生成每条公式的代入值与计算结果
// 返回结构：{ title, items: [{ text, substituted, result }] }
export const buildFormulaEvaluations = (parameters = {}, results = {}, opts = {}) => {
  const type = parameters?.protectionType || 'unknown';
  const t = typeof opts.t === 'function' ? opts.t : null;
  const TT = (key, fallback) => (t ? t(key) : fallback);
  const meta = getCalculationFormula(type, { t });

  // 曲线常数（与 protectionCalculations 保持一致，用于 IEC 60255 反时限评估）
  const CURVES = {
    'Normal Inverse': { k: 0.14, alpha: 0.02 },
    'Very Inverse': { k: 13.5, alpha: 1 },
    'Extremely Inverse': { k: 80, alpha: 2 },
    'Long-time Inverse': { k: 120, alpha: 1 }
  };
  const curveLabel = parameters.curveType || results.curveType || 'Normal Inverse';
  const curveConst = CURVES[curveLabel] || CURVES['Normal Inverse'];
  const formatTime = (tVal) => (tVal === Infinity ? TT('export.common.no_action', '不动作') : `${Number(tVal).toFixed(3)}s`);

  const items = [];

  if (type === 'overcurrent') {
    const rated = Number(parameters.ratedCurrent) || 0;
    const ct = Number(parameters.ctRatio) || 1;
    const fault = Number(parameters.faultCurrent) || 0;
    const secI = fault / ct;
    const Krel = 1.3; // 取中间值，与计算逻辑一致
    const Iop = (rated * Krel) / ct;
    const TMS = typeof parameters.timeMultiplier === 'number' ? parameters.timeMultiplier : (typeof results.timeMultiplier === 'number' ? results.timeMultiplier : 0.5);
    const M = Iop > 0 ? (secI / Iop) : 0;
    const time = (() => {
      if (M <= 1) return Infinity;
      const { k, alpha } = curveConst;
      return TMS * k / (Math.pow(M, alpha) - 1);
    })();

    items.push({
      text: TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
      substituted: `If=${fault}A, nCT=${ct}`,
      result: `If.min=${Number(secI).toFixed(3)}A`,
      explain: TT('export.report.formulas.common.if_min_explain', '最小一次故障电流 / CT变比')
    });
    items.push({
      text: TT('export.report.formulas.overcurrent.iop', '启动电流定值: Iop = Krel × Irated / nCT'),
      substituted: `Krel=${Krel}, Irated=${rated}A, nCT=${ct}`,
      result: `Iop=${Number(Iop).toFixed(3)}A`,
      explain: '启动电流 = 系数 × 额定一次电流 / CT变比'
    });
    items.push({
      text: TT('export.report.formulas.overcurrent.ksen', '灵敏系数: Ksen = If.min / Iop'),
      substituted: `If.min=${Number(secI).toFixed(3)}A, Iop=${Number(Iop).toFixed(3)}A`,
      result: `Ksen=${(Iop > 0 ? (secI / Iop) : 0).toFixed(2)}`,
      explain: '灵敏系数 = 最小故障电流 / 启动电流'
    });
    items.push({
      text: TT('export.report.formulas.overcurrent.time', '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)'),
      substituted: `TMS=${TMS}, M=I/Iop=${M.toFixed(2)}, 曲线族=${mapCurveLabel(curveLabel)}，k=${curveConst.k}，α=${curveConst.alpha}`,
      result: `t=${formatTime(time)}`,
      explain: '动作时间 = 时间倍数 × k / (M^α − 1)，其中 M=I/Iop，k、α 为曲线常数（IEC 60255）'
    });
  } else if (type === 'distance') {
    const U_kV = Number(parameters.systemVoltage) || 0;
    const I_A = Number(parameters.faultCurrent) || 0;
    const ct = Number(parameters.ctRatio) || 1;
    const pt = Number(parameters.ptRatio) || 1;
    const Isec = (I_A) / (ct || 1);
    const Zm_primary = I_A > 0 ? (U_kV * 1000) / I_A : 0;
    const Zm_secondary = Zm_primary * (ct / pt);
    const Zline = (typeof results.secondaryImpedance === 'number') ? Number(results.secondaryImpedance) : Zm_secondary;
    const Z1 = (typeof results.zone1Setting === 'number') ? Number(results.zone1Setting) : Zline;
    const Z2 = (typeof results.zone2Setting === 'number') ? Number(results.zone2Setting) : Zline;
    const Z3 = (typeof results.zone3Setting === 'number') ? Number(results.zone3Setting) : Zline;
    const K1 = Zline > 0 ? (Z1 / Zline) : 0;
    const K2 = Zline > 0 ? (Z2 / Zline) : 0;
    const K3 = Zline > 0 ? (Z3 / Zline) : 0;

    items.push({
      text: TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
      substituted: `If=${I_A}A, nCT=${ct}`,
      result: `If.min=${Number(Isec).toFixed(3)}A`,
      explain: TT('export.report.formulas.common.if_min_explain', '最小一次故障电流 / CT变比')
    });
    items.push({
      text: TT('export.report.formulas.distance.zm', '测量阻抗: Zm = U / I'),
      substituted: `U=${U_kV}kV (${(U_kV*1000).toFixed(0)}V), I=${I_A}A`,
      result: `Zm=${Number(Zm_primary).toFixed(3)}Ω；二次 Zm=${Number(Zm_secondary).toFixed(3)}Ω`,
      explain: '测量阻抗 = 电压 / 电流'
    });
    items.push({
      text: TT('export.report.formulas.distance.zset1', '第I段定值: Zset.I = Krel × Zline'),
      substituted: `Zline=${Number(Zline).toFixed(3)}Ω, Krel≈${K1.toFixed(2)}×`,
      result: `Zset.I=${Number(Z1).toFixed(3)}Ω`,
      explain: '第I段定值 = 覆盖比例 × 线路阻抗'
    });
    items.push({
      text: TT('export.report.formulas.distance.zset2', '第II段定值: Zset.II = Zline + Krel × Znext'),
      substituted: `Zline=${Number(Zline).toFixed(3)}Ω, Krel≈${K2.toFixed(2)}×`,
      result: `Zset.II=${Number(Z2).toFixed(3)}Ω`,
      explain: '第II段定值 = 线路阻抗 + 覆盖比例 × 下一段阻抗'
    });
    items.push({
      text: TT('export.report.formulas.distance.zset3', '第III段定值: Zset.III = Krel × Zload.min / Kss.max'),
      substituted: `近似按覆盖比例：Krel≈${K3.toFixed(2)}×, 基准=${Number(Zline).toFixed(3)}Ω`,
      result: `Zset.III=${Number(Z3).toFixed(3)}Ω`,
      explain: '第III段定值 = 覆盖比例 × 最小负荷阻抗 / 最大灵敏系数'
    });
  } else if (type === 'differential') {
    const Isd_sec = (typeof results.diffInstantaneousSecondary === 'number') ? Number(results.diffInstantaneousSecondary) : null;
    const Icd = (typeof results.differentialCurrentSetting === 'number') ? Number(results.differentialCurrentSetting) : null;
    const K = (typeof results.restraintSlope === 'number') ? Number(results.restraintSlope) : null;
    const Kb = (typeof results.secondHarmonicBlocking === 'number') ? Number(results.secondHarmonicBlocking) : null;

    items.push({
      text: TT('export.report.formulas.differential.id', '差动电流: Id = |I1 + I2|'),
      substituted: '需运行电流 I1/I2 数据',
      result: '—',
      explain: '差动电流 = 两端电流向量和的绝对值'
    });
    items.push({
      text: TT('export.report.formulas.differential.ir', '制动电流: Ir = (|I1| + |I2|) / 2'),
      substituted: '需运行电流 I1/I2 数据',
      result: '—',
      explain: '制动电流 = 两端电流绝对值平均'
    });
    items.push({
      text: TT('export.report.formulas.differential.instant_trip', '差动速断: Id > Isd'),
      substituted: `Isd(二次)=${Isd_sec != null ? `${Isd_sec.toFixed(3)}A` : '—'}`,
      result: `速断阈值=${Isd_sec != null ? `${Isd_sec.toFixed(3)}A` : '—'}`,
      explain: '速断判据 = 差动电流 > 速断阈值'
    });
    items.push({
      text: TT('export.report.formulas.differential.ratio_restraint', '比率制动: Id > Icd + K × (Ir - Ibr)'),
      substituted: `Icd=${Icd != null ? `${Icd.toFixed(2)}A` : '—'}, K=${K != null ? K.toFixed(2) : '—'}, Ibr≈Icd`,
      result: `阈值表达式 = Icd + K × (Ir - Ibr)`,
      explain: '比率制动判据 = 差动电流 > 动作门槛 + 斜率 × (制动电流 − 制动基准)'
    });
    items.push({
      text: TT('export.report.formulas.differential.harmonic_blocking', '二次谐波制动: I2/I1 > Kb'),
      substituted: `Kb=${Kb != null ? Kb.toFixed(2) : '—'}`,
      result: `判据阈值=${Kb != null ? Kb.toFixed(2) : '—'}`,
      explain: '二次谐波制动判据 = 谐波比值 > 门槛'
    });
  } else if (type === 'directional') {
    const U_kV = Number(parameters.systemVoltage) || 0;
    const I_A = Number(parameters.faultCurrent) || 0;
    const ct = Number(parameters.ctRatio) || 1;
    const pt = Number(parameters.ptRatio) || 1;
    const theta = typeof parameters.polarizingAngle === 'number' ? parameters.polarizingAngle : (typeof results.polarizingAngle === 'number' ? results.polarizingAngle : 30);
    const Vsec = (U_kV * 1000) / (pt || 1);
    const Isec = (I_A) / (ct || 1);
    const P = Vsec * Isec * Math.cos(theta * Math.PI/180);
    const dir = P >= 0 ? TT('export.report.direction.forward', '正向') : TT('export.report.direction.reverse', '反向');
    const rated = Number(parameters.ratedCurrent) || 0;
    const Iop = (rated * 1.1) / (ct || 1);
    const M = Iop > 0 ? (Isec / Iop) : 0;
    const TMS = typeof parameters.timeMultiplier === 'number' ? parameters.timeMultiplier : (typeof results.timeMultiplier === 'number' ? results.timeMultiplier : 0.5);
    const time = (() => {
      if (M <= 1) return Infinity;
      const { k, alpha } = curveConst;
      return TMS * k / (Math.pow(M, alpha) - 1);
    })();

    items.push({
      text: TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
      substituted: `If=${I_A}A, nCT=${ct}`,
      result: `If.min=${Number(Isec).toFixed(2)}A`,
      explain: TT('export.report.formulas.common.if_min_explain', '最小一次故障电流 / CT变比')
    });
    items.push({
      text: TT('export.report.formulas.directional.vsec', '二次电压: Vsec = U / nPT'),
      substituted: `U=${U_kV}kV (${(U_kV*1000).toFixed(0)}V), nPT=${pt}`,
      result: `Vsec=${Number(Vsec).toFixed(2)}V`,
      explain: '二次电压 = 一次电压 / PT变比'
    });
    items.push({
      text: TT('export.report.formulas.directional.isec', '二次电流: Isec = If / nCT'),
      substituted: `If=${I_A}A, nCT=${ct}`,
      result: `Isec=${Number(Isec).toFixed(2)}A`,
      explain: '二次电流 = 一次电流 / CT变比'
    });
    items.push({
      text: TT('export.report.formulas.directional.p_criterion', '相量有功分量: P = Re{V × I*} ≈ Vsec × Isec × cos(φ − θp)'),
      substituted: `Vsec=${Number(Vsec).toFixed(2)}V, Isec=${Number(Isec).toFixed(2)}A, θp=${theta}°`,
      result: `P≈${Number(P).toFixed(2)}，方向判据=${dir}`,
      explain: '相量有功分量 = Re{V × I*}（示意：cos(φ − θp)）'
    });
    items.push({
      text: TT('export.report.formulas.directional.iop', '启动电流: Iop = 1.1 × Irated / nCT'),
      substituted: `Irated=${rated}A, nCT=${ct}`,
      result: `Iop=${Number(Iop).toFixed(3)}A`,
      explain: '启动电流 = 系数 × 额定一次电流 / CT变比'
    });
    items.push({
      text: TT('export.report.formulas.directional.multiple', '倍数: M = Isec / Iop'),
      substituted: `Isec=${Number(Isec).toFixed(2)}A, Iop=${Number(Iop).toFixed(3)}A`,
      result: `M=${(Iop > 0 ? (Isec/Iop) : 0).toFixed(2)}`,
      explain: '倍数 = 二次电流 / 启动电流'
    });
    items.push({
      text: TT('export.report.formulas.directional.time', '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)'),
      substituted: `TMS=${TMS}, M=${M.toFixed(2)}, 曲线族=${mapCurveLabel(curveLabel)}，k=${curveConst.k}，α=${curveConst.alpha}`,
      result: `t=${formatTime(time)}`,
      explain: '动作时间 = 时间倍数 × k / (M^α − 1)，其中 M=I/Iop，k、α 为曲线常数（IEC 60255）'
    });
  } else if (type === 'zero_sequence') {
    const rated = Number(parameters.ratedCurrent) || 0;
    const ct = Number(parameters.ctRatio) || 1;
    const fault = Number(parameters.faultCurrent) || 0;
    const k0 = typeof parameters.k0 === 'number' ? parameters.k0 : (typeof results.k0 === 'number' ? results.k0 : 0.3);
    const Isec = (fault) / (ct || 1);
    const I0 = (fault * k0) / (ct || 1);
    const Iop0 = (rated * 0.4) / (ct || 1);
    const M = Iop0 > 0 ? (I0 / Iop0) : 0;
    const TMS = typeof parameters.timeMultiplier === 'number' ? parameters.timeMultiplier : (typeof results.timeMultiplier === 'number' ? results.timeMultiplier : 0.5);
    const time = (() => {
      if (M <= 1) return Infinity;
      const { k, alpha } = curveConst;
      return TMS * k / (Math.pow(M, alpha) - 1);
    })();

    items.push({
      text: TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
      substituted: `If=${fault}A, nCT=${ct}`,
      result: `If.min=${Number(Isec).toFixed(3)}A`,
      explain: TT('export.report.formulas.common.if_min_explain', '最小一次故障电流 / CT变比')
    });
    items.push({
      text: TT('export.report.formulas.zero_sequence.i0', '二次零序电流: I0 = k0 × If / nCT'),
      substituted: `k0=${k0}, If=${fault}A, nCT=${ct}`,
      result: `I0=${Number(I0).toFixed(3)}A`,
      explain: '近似：二次零序电流 = 零序系数 × 一次故障电流 / CT变比（系数需由网络零序参数或短路计算确定）'
    });
    items.push({
      text: TT('export.report.formulas.zero_sequence.iop0', '启动电流: Iop0 = 0.4 × Irated / nCT'),
      substituted: `Irated=${rated}A, nCT=${ct}`,
      result: `Iop0=${Number(Iop0).toFixed(3)}A`,
      explain: '启动电流 = 系数 × 额定一次电流 / CT变比'
    });
    items.push({
      text: TT('export.report.formulas.zero_sequence.multiple', '倍数: M = I0 / Iop0'),
      substituted: `I0=${Number(I0).toFixed(3)}A, Iop0=${Number(Iop0).toFixed(3)}A`,
      result: `M=${(Iop0 > 0 ? (I0/Iop0) : 0).toFixed(2)}`,
      explain: '倍数 = 二次零序电流 / 启动电流'
    });
    items.push({
      text: TT('export.report.formulas.zero_sequence.time', '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)'),
      substituted: `TMS=${TMS}, M=${M.toFixed(2)}, 曲线族=${mapCurveLabel(curveLabel)}，k=${curveConst.k}，α=${curveConst.alpha}`,
      result: `t=${formatTime(time)}`,
      explain: '动作时间 = 时间倍数 × k / (M^α − 1)，其中 M=I/Iop，k、α 为曲线常数（IEC 60255）'
    });
  } else if (type === 'longitudinal') {
    const rated = Number(parameters.ratedCurrent) || 0;
    const ct = Number(parameters.ctRatio) || 1;
    const fault = Number(parameters.faultCurrent) || 0;
    const Isec = (fault) / (ct || 1);
    const Iop = (rated * 1.2) / (ct || 1);
    const tdef = 0.05;

    items.push({
      text: TT('export.report.formulas.common.if_min', '最小故障电流（二次）: If.min = min(If) / nCT'),
      substituted: `If=${fault}A, nCT=${ct}`,
      result: `If.min=${Number(Isec).toFixed(3)}A`,
      explain: TT('export.report.formulas.common.if_min_explain', '最小一次故障电流 / CT变比')
    });
    items.push({
      text: TT('export.report.formulas.longitudinal.isec', '二次电流: Isec = If / nCT'),
      substituted: `If=${fault}A, nCT=${ct}`,
      result: `Isec=${Number(Isec).toFixed(3)}A`,
      explain: '二次电流 = 一次电流 / CT变比'
    });
    items.push({
      text: TT('export.report.formulas.longitudinal.id', '差动电流: Id = |I本端 + I对端|（单侧供电近似 Id ≈ Isec）'),
      substituted: `近似条件：单侧供电`,
      result: `Id≈${Number(Isec).toFixed(3)}A`,
      explain: '差动电流 ≈ 本端电流（单侧供电近似）'
    });
    items.push({
      text: TT('export.report.formulas.longitudinal.iop', '启动电流: Iop = 1.2 × Irated / nCT'),
      substituted: `Irated=${rated}A, nCT=${ct}`,
      result: `Iop=${Number(Iop).toFixed(3)}A`,
      explain: '启动电流 = 系数 × 额定一次电流 / CT变比'
    });
    items.push({
      text: TT('export.report.formulas.longitudinal.time', '动作时间: t = tdef（快速动作，典型 0.05s）'),
      substituted: `tdef≈0.05s`,
      result: `t=${tdef.toFixed(3)}s`,
      explain: '动作时间 = 固定值（快速动作）'
    });
  } else {
    items.push({ text: TT('export.report.formulas.default.placeholder', '请根据保护类型查看对应计算公式'), substituted: '', result: '' });
  }

  return { title: meta?.title || TT('export.report.formulas.title', '计算公式'), items };
};

// 结果标签格式化（统一来源：labelMappings）
export const formatResultLabel = (key, protectionType) => getResultLabel(key, protectionType);

// 曲线族中英双语映射（导出使用）
export const mapCurveLabel = (value) => {
  const map = {
    'Normal Inverse': '标准反时限 (Normal Inverse)',
    'Very Inverse': '非常反时限 (Very Inverse)',
    'Extremely Inverse': '极度反时限 (Extremely Inverse)',
    'Long-time Inverse': '长延时反时限 (Long-time Inverse)'
  };
  return map[value] || value;
};

// 导出值格式化（不动作/双语曲线族/对象过滤）
export const formatResultValueExport = (key, value, opts = {}) => {
  const t = typeof opts.t === 'function' ? opts.t : null;
  const TT = (k, f) => (t ? t(k) : f);
  if (key === 'operateTime') {
    return value === Infinity ? TT('export.common.no_action', '不动作') : value;
  }
  if (key === 'curveType') {
    return mapCurveLabel(value);
  }
  if (key === 'baseImpedanceMode') {
    const map = {
      fault: TT('export.result.base_impedance_mode.fault', '工况故障 (V/I)'),
      line: TT('export.result.base_impedance_mode.line', '线路参数 (R/X)')
    };
    return map[value] || value;
  }
  if (key === 'voltageLow') {
    return value ? TT('export.common.yes', '是') : TT('export.common.no', '否');
  }
  if (key === 'axesType') {
    const axesMap = {
      'I-t': TT('export.report.axes.i_t_label', 'I-t'),
      'Z': TT('export.report.axes.z_label', '阻抗平面 (Z)'),
      'Id-Ir': TT('export.report.axes.idir_label', '差动平面 (Id–Ir)')
    };
    return axesMap[value] || value;
  }
  if (key === 'zones' && Array.isArray(value)) {
    return value
      .map((z, idx) => {
        const label = z.label || `Zone ${idx + 1}`;
        const Z = Number(z.Z) || 0;
        const t0 = Number(z.thetaStartDeg);
        const t1 = Number(z.thetaEndDeg);
        const m = z.marginPct != null ? `, ${TT('export.report.z_plane.zone_margin_prefix', '裕度')}${z.marginPct}%` : '';
        return `${label}: Z=${Z.toFixed(3)}Ω, θ=${isFinite(t0) ? t0.toFixed(0) : '?'}~${isFinite(t1) ? t1.toFixed(0) : '?'}°${m}`;
      })
      .join('； ');
  }
  return typeof value === 'object' ? JSON.stringify(value) : value;
};

// 参数单位映射（用于 Markdown 参数说明）
const FIELD_UNIT_MAP = {
  systemVoltage: 'kV',
  ratedCurrent: 'A',
  ctRatio: '',
  ptRatio: '',
  faultCurrent: 'A',
  transformerCapacity: 'kVA',
  timeMultiplier: '',
  polarizingAngle: '°',
  k0: '',
  // 过电流高级可选输入
  maxLoadCurrent: 'A',
  pickupMultiplier: '',
  instantaneousMultiplier: '',
  lineR_ohm: 'Ω',
  lineX_ohm: 'Ω',
  lineAngleDeg: '°',
  lineLength_km: 'km',
  lineR_per_km_ohm: 'Ω/km',
  lineX_per_km_ohm: 'Ω/km'
};

// 生成参数范围说明条目
export const buildParamNotes = (p = {}, type = 'unknown') => {
  const items = [];
  Object.keys(FIELD_UNIT_MAP).forEach((field) => {
    const rng = getFieldRange(type, field);
    if (!rng) return;
    const unit = FIELD_UNIT_MAP[field] || '';
    const label = getParamLabel(field, type);
    items.push([label, `${rng[0]}–${rng[1]}${unit ? ` ${unit}` : ''}`]);
  });
  return items;
};

// 生成 Markdown 报告文本（优化：中文标签、过滤曲线点、不动作、双语曲线族）
export const buildMarkdownReport = ({ parameters, results, remark, name, extras = {} }, opts = {}) => {
  const type = parameters?.protectionType || 'unknown';
  const t = typeof opts.t === 'function' ? opts.t : null;
  const TT = (key, fallback) => (t ? t(key) : fallback);
  const typeName = getProtectionTypeName(type, { t });
  const now = new Date();
  const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
  const includeParamNotes = extras?.includeParamNotes !== false;
  const includeDeviceSource = extras?.includeDeviceSource !== false;

  // 如缺少结果，尝试重算
  let finalResults = results;
  if (!finalResults && parameters) {
    try {
      finalResults = calculateProtectionSettings(parameters);
    } catch (_) {
      finalResults = {};
    }
  }

  let md = `${TT('export.report.md.title', '# 继电保护计算报告')}\n\n`;
  md += `${TT('export.report.name_prefix', '- 名称: ')}${name && name.trim() ? name.trim() : TT('export.report.header.unnamed', '未命名')}\n`;
  md += `${TT('export.report.type_prefix', '- 保护类型: ')}${typeName}\n`;
  if (remark) md += `${TT('export.report.remark_prefix', '- 备注: ')}${remark}\n`;

  // 上下文小节：统一包含项目/线路/标签/版本戳/时间
  md += `\n${TT('export.report.context.title', '## 上下文')}\n`;
  md += `${TT('export.report.context.export_time_prefix', '- 导出时间: ')}${now.toLocaleString(t ? undefined : 'zh-CN')}\n`;
  {
    const project = String(extras.project || '').trim();
    const line = String(extras.line || '').trim();
    if (project) md += `${TT('export.report.context.project_prefix', '- 项目: ')}${project}\n`;
    if (line) md += `${TT('export.report.context.line_prefix', '- 线路: ')}${line}\n`;
  }
  if (Array.isArray(extras.tags) && extras.tags.length > 0) {
    md += `${TT('export.report.context.tags_prefix', '- 标签: ')}${extras.tags.join(', ')}\n`;
  }
  if (extras.versionStamp) {
    md += `${TT('export.report.context.version_stamp_prefix', '- 版本戳: ')}${extras.versionStamp}\n`;
  }
  if (extras.filenamePrefixResolved && String(extras.filenamePrefixResolved).trim()) {
    md += `${TT('export.report.context.filename_prefix_resolved_prefix', '- 导出前缀（解析后）: ')}${String(extras.filenamePrefixResolved).trim()}\n`;
  }

  // 自动整定变更日志（如有，且未被关闭）
  const includeAutoTuningLog = extras?.includeAutoTuningLog !== false;
  const tuningEvents = Array.isArray(extras.autoTuningLogEvents) ? extras.autoTuningLogEvents : [];
  if (includeAutoTuningLog && tuningEvents.length > 0) {
    const fmtParamValue = (key, value) => {
      if (value == null) return '—';
      if (key === 'curveType') return mapCurveLabel(value);
      if (typeof value === 'number' && isFinite(value)) {
        // 对常见参数做轻量格式化
        if (/timeMultiplier|tms/i.test(String(key))) return Number(value).toFixed(3);
        return String(value);
      }
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };
    md += `\n${TT('export.report.autotune.title', '## 自动整定变更日志')}\n`;
    tuningEvents.forEach((evt, idx) => {
      const actionText = evt?.action === 'apply'
        ? TT('export.report.autotune.action.apply', '应用建议')
        : (evt?.action === 'rollback' ? TT('export.report.autotune.action.rollback', '回滚至快照') : String(evt?.action || TT('export.report.autotune.action.other', '事件')));
      const timeText = (() => { try { return new Date(evt?.time).toLocaleString(t ? undefined : 'zh-CN'); } catch (_) { return String(evt?.time || ''); } })();
      md += `\n### ${TT('export.report.autotune.event_prefix', '事件')} ${idx + 1}${TT('export.report.autotune.event_delimiter', '：')}${actionText}（${timeText}）\n`;
      if (evt?.type) md += `${TT('export.report.type_prefix', '- 保护类型: ')}${getProtectionTypeName(evt.type, { t })}\n`;
      if (evt?.source) md += `${TT('export.report.autotune.source_prefix', '- 来源: ')}${String(evt.source)}\n`;
      if (evt?.goal) md += `${TT('export.report.autotune.goal_prefix', '- 目标: ')}${evt.goal}\n`;
      if (Array.isArray(evt?.appliedFields) && evt.appliedFields.length > 0) {
        md += `${TT('export.report.autotune.applied_fields_prefix', '- 应用字段: ')}${evt.appliedFields.join(', ')}\n`;
      }
      if (Array.isArray(evt?.rationale) && evt.rationale.length > 0) {
        md += `${TT('export.report.autotune.rationale_prefix', '- 理由: ')}\n`;
        evt.rationale.forEach((r) => { md += `  - ${r}\n`; });
      }
      if (Array.isArray(evt?.warnings) && evt.warnings.length > 0) {
        md += `${TT('export.report.warnings_prefix', '- 警告: ')}\n`;
        evt.warnings.forEach((w) => { md += `  - ⚠️ ${w}\n`; });
      }
      if (evt?.userAdjustments && typeof evt.userAdjustments === 'object' && Object.keys(evt.userAdjustments).length > 0) {
        md += `${TT('export.report.autotune.user_adjustments_prefix', '- 用户后续调整: ')}\n`;
        Object.entries(evt.userAdjustments).forEach(([k, v]) => {
          const label = getParamLabel(k, parameters?.protectionType || evt?.type || 'unknown');
          md += `  - ${label}: ${fmtParamValue(k, v)}\n`;
        });
      }
      const changes = Array.isArray(evt?.changes) ? evt.changes : [];
      const reverted = Array.isArray(evt?.reverted) ? evt.reverted : [];
      const diffs = changes.length > 0 ? changes : reverted;
      if (diffs.length > 0) {
        md += `${TT('export.report.autotune.param_diffs_prefix', '- 参数差异: ')}\n`;
        diffs.forEach((c) => {
          const label = c?.label || getParamLabel(c?.key, parameters?.protectionType || evt?.type || 'unknown');
          md += `  - ${label}: ${fmtParamValue(c?.key, c?.from)} → ${fmtParamValue(c?.key, c?.to)}\n`;
        });
      }
    });
  }

  // 自动整定参数摘要（如 extras 提供）
  if (extras && extras.autoTuningParamSummary && Array.isArray(extras.autoTuningParamSummary.changedParams)) {
    const s = extras.autoTuningParamSummary;
    md += `\n${TT('export.report.autotune.summary.title', '## 自动整定参数摘要')}\n`;
    if (typeof s.applyCount === 'number') md += `${TT('export.report.autotune.summary.apply_count_prefix', '- 应用次数: ')}${s.applyCount}\n`;
    if (typeof s.rollbackCount === 'number') md += `${TT('export.report.autotune.summary.rollback_count_prefix', '- 回滚次数: ')}${s.rollbackCount}\n`;
    if (s.lastAction) {
      const actText = s.lastAction === 'apply' ? TT('export.report.autotune.action.apply', '应用建议')
        : (s.lastAction === 'rollback' ? TT('export.report.autotune.action.rollback', '回滚至快照') : String(s.lastAction));
      const timeText = (() => { try { return new Date(s.lastTime).toLocaleString(t ? undefined : 'zh-CN'); } catch (_) { return String(s.lastTime || ''); } })();
      md += `${TT('export.report.autotune.summary.last_action_prefix', '- 最近操作: ')}${actText}${timeText ? `（${timeText}）` : ''}\n`;
    }
    if (Array.isArray(s.rationale) && s.rationale.length > 0) {
      md += `${TT('export.report.autotune.rationale_prefix', '- 理由: ')}\n`;
      s.rationale.forEach((r) => { md += `  - ${r}\n`; });
    }
    if (Array.isArray(s.warnings) && s.warnings.length > 0) {
      md += `${TT('export.report.warnings_prefix', '- 警告: ')}\n`;
      s.warnings.forEach((w) => { md += `  - ⚠️ ${w}\n`; });
    }
    if (s.changedParams.length > 0) {
      md += `${TT('export.report.autotune.summary.changed_params_prefix', '- 汇总参数: ')}\n`;
      s.changedParams.forEach((c) => {
        const label = c?.label || getParamLabel(c?.key, type);
        const val = formatResultValueExport(c?.key, c?.to, { t });
        md += `  - ${label}: ${val}\n`;
      });
    }
  }

  // 设备来源（来自设备库详情或仅ID引用）
  if (includeDeviceSource && (extras.deviceDetails || extras.deviceRefs)) {
    md += `\n${TT('export.report.device.title', '## 设备来源')}\n`;
    const fd = extras.deviceDetails;
    if (fd) {
      md += `${TT('export.report.device.feeder_prefix', '- 馈线: ')}${(fd.feeder?.name || fd.feeder?.id || '未知')}${fd.feeder?.id ? ` (${fd.feeder.id})` : ''}\n`;
      if (fd.bus?.voltage_kV != null) md += `${TT('export.report.device.bus_voltage_prefix', '- 母线电压: ')}${fd.bus.voltage_kV} kV\n`;
      if (fd.ratedCurrent != null) md += `${TT('export.report.device.rated_current_prefix', '- 额定电流: ')}${fd.ratedCurrent} A\n`;
      if (fd.ctRatio != null) md += `${TT('export.report.device.ct_ratio_prefix', '- CT变比: 1:')}${fd.ctRatio}\n`;
      if (fd.source) md += `${TT('export.report.device.source_prefix', '- 电源短路容量: ')}${fd.source.S_sc_MVA} MVA；X/R=${fd.source.X_R}\n`;
      if (fd.transformer) md += `${TT('export.report.device.transformer_prefix', '- 变压器: ')}${fd.transformer.S_tr_MVA} MVA；uk=${fd.transformer.uk_percent}%；组别=${fd.transformer.vector_group}${fd.transformer.secondary_neutral_ground ? `；中性点接地=${fd.transformer.secondary_neutral_ground}` : ''}\n`;
      if (fd.line) md += `${TT('export.report.device.line_prefix', '- 线路: ')}长度=${fd.line.length_km} km；R=${fd.line.R_per_km_ohm} Ω/km；X=${fd.line.X_per_km_ohm} Ω/km${fd.line.z0_factor != null ? `；零序系数=${fd.line.z0_factor}` : ''}\n`;
      if (fd.ground) md += `${TT('export.report.device.ground_prefix', '- 接地阻抗: ')}R=${fd.ground.R_ohm} Ω；X=${fd.ground.X_ohm} Ω\n`;
    } else {
      const dr = extras.deviceRefs || {};
      if (dr.feeder_id) md += `${TT('export.report.device.feeder_id_prefix', '- 馈线ID: ')}${dr.feeder_id}\n`;
      if (dr.ct_id) md += `${TT('export.report.device.ct_id_prefix', '- CT ID: ')}${dr.ct_id}\n`;
      if (dr.pt_id) md += `${TT('export.report.device.pt_id_prefix', '- PT ID: ')}${dr.pt_id}\n`;
      if (dr.relay_id) md += `${TT('export.report.device.relay_id_prefix', '- 继电器ID: ')}${dr.relay_id}\n`;
    }
  }

  // 设备校核（阻断/提示）
  const devWarns = Array.isArray(extras.deviceWarnings) ? extras.deviceWarnings : [];
  const devBlocks = Array.isArray(extras.deviceBlockers) ? extras.deviceBlockers : [];
  if (devWarns.length > 0 || devBlocks.length > 0) {
    md += `\n${TT('export.report.device_check.title', '## 设备校核')}\n`;
    if (devBlocks.length > 0) {
      md += `${TT('export.report.device_check.blockers_title', '- 阻断项：请修正后再计算')}\n`;
      devBlocks.forEach((b) => { md += `  - ⛔ ${b}\n`; });
    }
    if (devWarns.length > 0) {
      md += `${TT('export.report.device_check.warnings_title', '- 提示项：建议对齐设备库参数')}\n`;
      devWarns.forEach((w) => { md += `  - ⚠️ ${w}\n`; });
    }
  }

  // 输入参数（中文标签 + 单位 + 条件项）
  md += `\n${TT('export.report.input.title', '## 输入参数')}\n`;
  const p = parameters || {};
  if (p.systemVoltage != null) md += `${TT('export.report.input.system_voltage_prefix', '- 系统电压: ')}${p.systemVoltage} kV\n`;
  if (p.ratedCurrent != null) md += `${TT('export.report.input.rated_current_prefix', '- 额定电流: ')}${p.ratedCurrent} A\n`;
  if (p.ctRatio != null) md += `${TT('export.report.input.ct_ratio_prefix', '- CT变比: 1:')}${p.ctRatio}\n`;
  if (p.ptRatio != null) md += `${TT('export.report.input.pt_ratio_prefix', '- PT变比: 1:')}${p.ptRatio}\n`;
  if (p.faultCurrent != null) md += `${TT('export.report.input.fault_current_prefix', '- 故障电流: ')}${p.faultCurrent} A\n`;
  if (p.transformerCapacity != null) md += `${TT('export.report.input.transformer_capacity_prefix', '- 变压器容量: ')}${p.transformerCapacity} kVA\n`;
  md += `${TT('export.report.type_prefix', '- 保护类型: ')}${typeName}\n`;
  if (['overcurrent','directional','zero_sequence'].includes(type)) {
    md += `${TT('export.report.input.curve_family_prefix', '- 反时限曲线族: ')}${mapCurveLabel(p.curveType || 'Normal Inverse')}\n`;
    if (p.timeMultiplier != null) md += `${TT('export.report.input.tms_prefix', '- 时间倍数 (TMS): ')}${p.timeMultiplier}\n`;
  }
  if (type === 'overcurrent') {
    if (p.maxLoadCurrent != null) md += `${TT('export.report.input.max_load_current_prefix', '- 最大负荷电流: ')}${p.maxLoadCurrent} A\n`;
    if (p.pickupMultiplier != null) md += `${TT('export.report.input.pickup_multiplier_prefix', '- 启动倍数: ')}${p.pickupMultiplier}\n`;
    if (p.instantaneousMultiplier != null) md += `${TT('export.report.input.instantaneous_multiplier_prefix', '- 瞬时倍数: ')}${p.instantaneousMultiplier}\n`;
  }
  if (type === 'directional' && p.polarizingAngle != null) md += `${TT('export.report.input.polarizing_angle_prefix', '- 极化角 (°): ')}${p.polarizingAngle}\n`;
  if (type === 'zero_sequence' && p.k0 != null) md += `${TT('export.report.input.k0_prefix', '- 零序比例系数 k0: ')}${p.k0}\n`;

  // 参数说明（范围提示，按类型显示）
  const paramNotes = buildParamNotes(p, type);
  if (includeParamNotes && paramNotes.length > 0) {
    md += `\n${TT('export.report.param_notes.title', '## 参数说明')}\n`;
    paramNotes.forEach(([label, rangeText]) => {
      md += `- ${label}: ${rangeText}\n`;
    });
  }

  // 计算结果（过滤曲线点 + 值格式化）
  md += `\n${TT('export.report.results.title', '## 计算结果')}\n`;
  Object.entries(finalResults || {})
    .filter(([k]) => k !== 'curvePoints')
    .forEach(([k, v]) => {
      const label = formatResultLabel(k, type);
      const value = formatResultValueExport(k, v);
      md += `- ${label}: ${value}\n`;
    });

  // 工程校核提示
  const engWarns = Array.isArray(extras.engineeringWarnings) ? extras.engineeringWarnings : [];
  if (engWarns.length > 0) {
    md += `\n${TT('export.report.engineering_warnings.title', '## 工程校核提示')}\n`;
    engWarns.forEach((w) => { md += `- ⚠️ ${w}\n`; });
  }

  // 多坐标系兼容补充说明与图例
  const axesType = finalResults?.axesType || parameters?.axesType;
  if (axesType === 'Z') {
    md += `\n${TT('export.report.z_plane.title', '## 阻抗平面图 (R/X/θ)')}\n`;
    md += `${TT('export.report.z_plane.axes_desc', '- 坐标轴: R(Ω)/X(Ω)，原点为测量基准；线路阻抗角以虚线标示。')}\n`;
    if (Array.isArray(finalResults?.zones) && finalResults.zones.length > 0) {
      md += `${TT('export.report.z_plane.zones_title', '- 分区定值: ')}\n`;
      finalResults.zones.forEach((z, idx) => {
        const label = z.label || `Zone ${idx + 1}`;
        const Z = Number(z.Z) || 0;
        const t0 = Number(z.thetaStartDeg);
        const t1 = Number(z.thetaEndDeg);
        const m = z.marginPct != null ? `，裕度${z.marginPct}%` : '';
        md += `  - ${label}: Z=${Z.toFixed(3)}Ω，θ=${isFinite(t0) ? t0.toFixed(0) : '?'}~${isFinite(t1) ? t1.toFixed(0) : '?'}°${m}\n`;
      });
    }
    if (extras.zPlaneImageDataUrl && typeof extras.zPlaneImageDataUrl === 'string') {
      md += `\n![阻抗平面图](${extras.zPlaneImageDataUrl})\n`;
    } else if (extras.chartImageDataUrl) {
      md += `\n![${TT('export.report.chart.image_alt', '图表')}](${extras.chartImageDataUrl})\n`;
    }
  } else if (axesType === 'Id-Ir') {
    md += `\n${TT('export.report.chart.title', '## 差动特性图 (Id–Ir)')}\n`;
    md += `${TT('export.report.idir.axes_desc', '- 横轴为制动电流 Ir(A)，纵轴为差动电流 Id(A)；分段斜率与速断元件按曲线标注。')}\n`;
    if (Array.isArray(finalResults?.characteristic?.segments)) {
      md += `${TT('export.report.idir.segments_title', '- 特性分段: ')}\n`;
      finalResults.characteristic.segments.forEach((seg, idx) => {
        const k = seg.slope != null ? `斜率K=${seg.slope}` : '';
        const th = seg.threshold != null ? `阈值=${seg.threshold}A` : '';
        const label = seg.label || `SEG${idx + 1}`;
        const parts = [label, k, th].filter(Boolean).join('； ');
        md += `  - ${parts}\n`;
      });
      // 参数表（Markdown 表格）
      md += `\n| ${TT('export.report.idir.table.headers.segment', '段序')} | ${TT('export.report.idir.table.headers.slope', '斜率K')} | ${TT('export.report.idir.table.headers.threshold', '阈值 Ir(A)')} | ${TT('export.report.idir.table.headers.note', '说明')} |\n`;
      md += `|---:|---:|---:|:---|\n`;
      finalResults.characteristic.segments.forEach((seg, idx) => {
        const label = seg.label || `SEG${idx + 1}`;
        const k = seg.slope != null && isFinite(seg.slope) ? Number(seg.slope).toFixed(2) : '-';
        const th = seg.threshold != null && isFinite(seg.threshold) ? Number(seg.threshold).toFixed(3) : '∞';
        md += `| ${label} | ${k} | ${th} | ${seg.note || ''} |\n`;
      });
    }
    if (extras.idIrImageDataUrl && typeof extras.idIrImageDataUrl === 'string') {
      md += `\n![${TT('export.report.idir.image_alt', '差动特性图')}](${extras.idIrImageDataUrl})\n`;
    } else if (extras.chartImageDataUrl) {
      md += `\n![${TT('export.report.chart.image_alt', '图表')}](${extras.chartImageDataUrl})\n`;
    }
  } else {
    // I-t 或未指定，沿用通用图表输出
    if (extras.chartImageDataUrl && typeof extras.chartImageDataUrl === 'string') {
      md += `\n${TT('export.report.chart.title', '## 图表')}\n`;
      md += `![${TT('export.report.chart.image_alt', '图表')}](${extras.chartImageDataUrl})\n`;
    }
    if (extras.curveImageDataUrl && typeof extras.curveImageDataUrl === 'string') {
      md += `\n${TT('export.report.curve_screenshot.title', '## 反时限曲线截图')}\n`;
      md += `![${TT('export.report.curve_screenshot.image_alt', '曲线')}](${extras.curveImageDataUrl})\n`;
    }
  }

  // 图表截图（可选）
  // 条形图（可选）
  if (extras.barImageDataUrl && typeof extras.barImageDataUrl === 'string') {
    md += `\n${TT('export.report.bar.title', '## 条形图')}\n`;
    md += `![条形图](${extras.barImageDataUrl})\n`;
    // 数据摘要（Top5 数值型结果）
    try {
      const entries = Object.entries(finalResults || {})
        .filter(([k, v]) => typeof v === 'number' && isFinite(v))
        .map(([k, v]) => ({ key: k, label: formatResultLabel(k, type), value: Number(v) }))
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
        .slice(0, 5);
      if (entries.length > 0) {
        md += `\n${TT('export.report.bar.summary.title', '## 条形图数据摘要')}\n`;
        const fmt = (key, value) => {
          const s = String(key || '').toLowerCase();
          const v = Number(value) || 0;
          if (/(time|operate|时限|时间|tms)/i.test(s)) {
            return v < 1 ? `${(v * 1000).toFixed(0)}ms` : `${v.toFixed(2)}s`;
          }
          if (/(current|电流|ir|id)/i.test(s)) {
            return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(2)}kA` : `${v.toFixed(2)}A`;
          }
          if (/(impedance|阻抗|ohm|Ω)/i.test(s)) {
            return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(2)}kΩ` : `${v.toFixed(2)}Ω`;
          }
          if (Math.abs(v) >= 1000) {
            const units = ['', 'k', 'M', 'G'];
            let val = v; let idx = 0;
            while (Math.abs(val) >= 1000 && idx < units.length - 1) { val /= 1000; idx++; }
            return `${val.toFixed(2)}${units[idx]}`;
          }
          return v.toFixed(2);
        };
        entries.forEach((e) => {
          md += `- ${e.label}: ${fmt(e.key, e.value)}\n`;
        });
      }
    } catch (_) {}
  }
  if (extras.chartImageDataUrl && typeof extras.chartImageDataUrl === 'string') {
    md += `\n${TT('export.report.chart.title', '## 图表')}\n`;
    md += `![${TT('export.report.chart.image_alt', '图表')}](${extras.chartImageDataUrl})\n`;
  }
  if (extras.curveImageDataUrl && typeof extras.curveImageDataUrl === 'string') {
    md += `\n${TT('export.report.curve_screenshot.title', '## 反时限曲线截图')}\n`;
    md += `![${TT('export.report.curve_screenshot.image_alt', '曲线')}](${extras.curveImageDataUrl})\n`;
  }

  // 批注摘要
  if (extras.comments && String(extras.comments).trim()) {
    md += `\n${TT('export.report.comments.title', '## 批注')}\n`;
    md += `${String(extras.comments).trim()}\n`;
  }

  // 选择性校核矩阵（可选）
  const includeSelectivity = extras?.includeSelectivity !== false;
  const matrix = extras?.selectivityMatrix;
  if (includeSelectivity && matrix && Array.isArray(matrix.pairs) && matrix.pairs.length > 0) {
    md += `\n${TT('export.report.selectivity.title', '## 选择性校核')}\n`;
    const ctxProject = String(extras.project || '').trim();
    const ctxLine = String(extras.line || '').trim();
    if (ctxProject || ctxLine || extras.sortText) {
      md += `${TT('export.report.selectivity.context_prefix', '- 上下文: ')} `;
      const parts = [];
      if (ctxProject) parts.push(`${TT('export.report.selectivity.project_prefix', '项目 ')}${ctxProject}`);
      if (ctxLine) parts.push(`${TT('export.report.selectivity.line_prefix', '线路 ')}${ctxLine}`);
      if (extras.sortText) parts.push(`${TT('export.report.selectivity.sort_prefix', '排序 ')}${extras.sortText}`);
      md += parts.join('； ') + `\n`;
    }
    md += `${TT('export.report.selectivity.min_margin_prefix', '- 最小裕度: ')}${typeof matrix.minMargin === 'number' ? matrix.minMargin : 0.3}s\n`;
    md += `\n| ${TT('export.report.selectivity.table.headers.downstream', '下级')} | ${TT('export.report.selectivity.table.headers.upstream', '上级')} | ${TT('export.report.selectivity.table.headers.worst_margin', '最差裕度(s)')} | ${TT('export.report.selectivity.table.headers.worst_at_i', '最差点(A)')} | ${TT('export.report.selectivity.table.headers.pass', '通过')} | ${TT('export.report.selectivity.table.headers.recommend_tms', '建议TMS')} | ${TT('export.report.selectivity.table.headers.note', '备注')} |\n`;
    md += `|:---|:---|---:|---:|:---:|---:|:---|\n`;
    matrix.pairs.forEach(p => {
      const worstMargin = p.worstMargin == null ? '—' : String(p.worstMargin);
      const worstAtI = p.worstAtI == null ? '—' : String(p.worstAtI);
      const okText = p.ok ? TT('export.common.yes', '是') : TT('export.common.no', '否');
      const recText = (p.recommendation && typeof p.recommendation.recommended === 'number')
        ? `${p.recommendation.recommended}${p.recommendation.capped ? TT('export.common.capped', '（受限）') : ''}`
        : '—';
      const note = `${TT('export.report.selectivity.note.min_margin_prefix', '最小裕度 ')}${p.minMargin}s`;
      md += `| ${p.downstream} | ${p.upstream} | ${worstMargin} | ${worstAtI} | ${okText} | ${recText} | ${note} |\n`;
    });
    if (Array.isArray(matrix.adjustments) && matrix.adjustments.length > 0) {
      md += `\n${TT('export.report.selectivity.adjustments.title', '### 顺序调整建议')}\n`;
      matrix.adjustments.forEach(adj => {
        md += `- 上级“${adj.label}” TMS：${adj.from} → ${adj.to}${adj.capped ? TT('export.common.capped', '（受限）') : ''}；${TT('export.report.selectivity.note.min_margin_prefix', '最小裕度 ')}${adj.minMargin}s\n`;
      });
    }
  }

  // 计算公式
  md += `\n${TT('export.report.formulas.title', '## 计算公式')}\n`;
  getCalculationFormula(type, { t }).formulas.forEach((f) => {
    md += `- ${f}\n`;
  });

  const safeName = (name || '').trim().replace(/[\\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 50) || 'unnamed';
  const filename = `${safeName}_${ts}.md`;
  return { md, filename };
}

// 触发文本文件下载
export const downloadTextFile = (text, filename, mimeType = 'text/plain;charset=utf-8') => {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
export const exportElementToPdf = async ({ element, name, type, title = '继电保护计算报告', margin = 10, watermark = null, appendTimestamp = true, t = null }) => {
  const typeName = getProtectionTypeName(type, { t });
  const timestamp = new Date().toLocaleString(t ? undefined : 'zh-CN');
  const { default: html2canvas } = await import('html2canvas');
  const { default: jsPDF } = await import('jspdf');
  const baseCanvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
  const imgWidthPx = baseCanvas.width;
  const imgHeightPx = baseCanvas.height;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidthMm = pageWidth - margin * 2;
  const headerHeightMm = 14; // 两行页眉空间（以图像绘制，避免中文乱码）
  const footerHeightMm = 8;
  const mmPerPx = contentWidthMm / imgWidthPx;
  const headerHeightPx = Math.ceil(headerHeightMm / mmPerPx);
  const pageContentHeightMm = pageHeight - margin * 2 - headerHeightMm - footerHeightMm;
  const pageContentHeightPx = Math.floor(pageContentHeightMm / mmPerPx);

  const totalPages = Math.max(1, Math.ceil(imgHeightPx / pageContentHeightPx));

  let remainingHeightPx = imgHeightPx;
  let pageIndex = 0;
  while (remainingHeightPx > 0) {
    const sliceHeightPx = Math.min(pageContentHeightPx, remainingHeightPx);
    // 先截取内容片段
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = imgWidthPx;
    sliceCanvas.height = sliceHeightPx;
    const sliceCtx = sliceCanvas.getContext('2d');
    sliceCtx.fillStyle = '#ffffff';
    sliceCtx.fillRect(0, 0, imgWidthPx, sliceHeightPx);
    sliceCtx.drawImage(
      baseCanvas,
      0,
      pageIndex * pageContentHeightPx,
      imgWidthPx,
      sliceHeightPx,
      0,
      0,
      imgWidthPx,
      sliceHeightPx
    );

    // 组合页画布：页眉(中文) + 内容片段
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = imgWidthPx;
    pageCanvas.height = sliceHeightPx + headerHeightPx;
    const ctx = pageCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

    // 绘制页眉文字，使用系统中文字体，避免 jsPDF 中文乱码
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';
    ctx.font = '600 18px "Microsoft YaHei", "PingFang SC", "Noto Sans SC", Arial, sans-serif';
    const titleText = t ? t('export.report.header.title') : title;
    ctx.fillText(titleText, 16, 6);
    ctx.font = '400 14px "Microsoft YaHei", "PingFang SC", "Noto Sans SC", Arial, sans-serif';
    const unnamedText = t ? t('export.report.header.unnamed') : '未命名';
    const headerInfo = `${t ? t('export.report.header.name_label') : '名称'}: ${name && name.trim() ? name.trim() : unnamedText} | ${t ? t('export.report.header.type_label') : '类型'}: ${typeName} | ${t ? t('export.report.header.time_label') : '时间'}: ${timestamp}`;
    ctx.fillText(headerInfo, 16, 28);
    // 页眉分隔线
    ctx.strokeStyle = '#B4B4B4';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(8, headerHeightPx - 2);
    ctx.lineTo(imgWidthPx - 8, headerHeightPx - 2);
    ctx.stroke();

    // 绘制内容片段于页眉下方
    ctx.drawImage(sliceCanvas, 0, headerHeightPx);

    // 绘制水印（置于内容上方，透明度较低）
    if (watermark && typeof watermark === 'string' && watermark.trim()) {
      ctx.save();
      ctx.globalAlpha = 0.08;
      ctx.translate(pageCanvas.width / 2, headerHeightPx + sliceHeightPx / 2);
      ctx.rotate(-Math.PI / 8);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#000000';
      ctx.font = '700 64px "Microsoft YaHei", "PingFang SC", "Noto Sans SC", Arial, sans-serif';
      ctx.fillText(watermark.trim(), 0, 0);
      ctx.restore();
    }

    const pageImgData = pageCanvas.toDataURL('image/png');
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(pageImgData, 'PNG', margin, margin, contentWidthMm, pageCanvas.height * mmPerPx);

    // 页脚页码（使用 ASCII，避免中文乱码）
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Page ${pageIndex + 1} / ${totalPages}`, pageWidth - margin - 40, pageHeight - margin - 2);

    remainingHeightPx -= sliceHeightPx;
    pageIndex++;
  }

  const nowSave = new Date();
  const ts = `${nowSave.getFullYear()}${String(nowSave.getMonth()+1).padStart(2,'0')}${String(nowSave.getDate()).padStart(2,'0')}_${String(nowSave.getHours()).padStart(2,'0')}${String(nowSave.getMinutes()).padStart(2,'0')}`;
  const safeName = (name || '').trim().replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 50);
  pdf.save(`${(safeName || 'unnamed')}${appendTimestamp ? `_${ts}` : ''}.pdf`);
}
// 内联样式（HTML/Word 导出用，覆盖导出报告核心样式）
const EXPORT_REPORT_INLINE_CSS = `
.export-report { font-family: "Microsoft YaHei", "PingFang SC", "Noto Sans SC", Arial, sans-serif; color: #000; font-size: 14px; line-height: 1.6; }
.export-report .h-section { margin-bottom: 16px; padding-bottom: 8px; }
.export-report .h-title { font-size: 16px; font-weight: 600; border-left: 3px solid #1677ff; padding-left: 8px; margin-bottom: 8px; }
.export-report .h-sub { font-size: 13px; color: #666; }
.export-report .h-remark { font-size: 13px; color: #666; margin-top: 6px; }
.export-report .h-list { list-style: none; padding: 0; margin: 0; }
.export-report .h-list li { margin: 4px 0; }
.export-report .h-curve-svg { display: block; width: 100%; max-width: 680px; background: #fff; }
.h-bar-svg { display: block; width: 100%; max-width: 680px; background: #fff; }
.report-cover { text-align: center; padding: 40px 20px 24px; border: 2px solid #1677ff; border-radius: 8px; margin-bottom: 16px; background: #f7fbff; }
.cover-logo { max-height: 64px; margin-bottom: 12px; }
.cover-title { font-size: 22px; font-weight: 700; color: #0b1d36; }
.cover-sub { font-size: 14px; color: #4b5b78; margin-top: 4px; }
.cover-company { font-size: 13px; color: #4b5b78; margin-top: 8px; }
.cover-date { font-size: 12px; color: #8aa1bf; margin-top: 6px; }
.report-toc .toc-list { margin: 8px 0 0 16px; }
`;

const buildHtmlDocument = ({ innerHTML, title = '继电保护计算报告', includeCss = true }) => {
  const style = includeCss ? `<style>${EXPORT_REPORT_INLINE_CSS}</style>` : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${title}</title>${style}</head><body>${innerHTML}</body></html>`;
};

export const exportElementToHtml = ({ element, name, title = '继电保护计算报告', includeCss = true, appendTimestamp = true, t = null }) => {
  const innerHTML = element.innerHTML || '';
  const titleText = t ? (t('export.report.header.title') || title) : title;
  const doc = buildHtmlDocument({ innerHTML, title: titleText, includeCss });
  const safeName = (name || '').trim().replace(/[\\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 50) || 'unnamed';
  const now = new Date();
  const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
  downloadTextFile(doc, `${safeName}${appendTimestamp ? `_${ts}` : ''}.html`, 'text/html;charset=utf-8');
};

export const exportElementToWord = ({ element, name, title = '继电保护计算报告', includeCss = true, appendTimestamp = true, t = null }) => {
  const innerHTML = element.innerHTML || '';
  const titleText = t ? (t('export.report.header.title') || title) : title;
  const doc = buildHtmlDocument({ innerHTML, title: titleText, includeCss });
  const safeName = (name || '').trim().replace(/[\\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 50) || 'unnamed';
  const now = new Date();
  const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
  downloadTextFile(doc, `${safeName}${appendTimestamp ? `_${ts}` : ''}.doc`, 'application/msword');
};

export const captureElementToPngDataUrl = async (element, { scale = 2, background = '#ffffff' } = {}) => {
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(element, { scale, useCORS: true, backgroundColor: background });
  return canvas.toDataURL('image/png');
};

// 选择性矩阵导出（Markdown）
export const buildSelectivityMatrixMarkdown = (matrix = {}, { title = '多级选择性校核矩阵', minMargin = 0.3, context = {}, t = null } = {}) => {
  const TT = (key, fallback) => (typeof t === 'function' ? (t(key) || fallback) : fallback);
  const lines = [];
  lines.push(`# ${title}`);
  const project = (context.project || '').trim();
  const line = (context.line || '').trim();
  const versionStamp = (context.versionStamp || '').trim();
  const nowStr = typeof t === 'function' ? new Date().toLocaleString() : new Date().toLocaleString('zh-CN');
  if (project) lines.push(`${TT('export.report.context.project_prefix', '- 项目: ')}${project}`);
  if (line) lines.push(`${TT('export.report.context.line_prefix', '- 线路: ')}${line}`);
  if (versionStamp) lines.push(`${TT('export.report.context.version_stamp_prefix', '- 版本戳: ')}${versionStamp}`);
  lines.push(`${TT('export.report.context.export_time_prefix', '- 导出时间: ')}${nowStr}`);
  lines.push(`${TT('export.report.selectivity.min_margin_prefix', '- 最小裕度: ')}${minMargin}s`);
  lines.push('');
  if (!Array.isArray(matrix?.pairs) || matrix.pairs.length === 0) {
    lines.push(TT('export.report.selectivity.no_data', '无可用的相邻链路校核数据。'));
  } else {
    const yesNo = matrix.overallOk ? TT('export.common.yes', '是') : TT('export.common.no', '否');
    lines.push(`${TT('export.report.selectivity.overall_ok_prefix', '总体是否满足：')}${yesNo}`);
    lines.push('');
    const hDown = TT('export.report.selectivity.table.headers.downstream', '下级');
    const hUp = TT('export.report.selectivity.table.headers.upstream', '上级');
    const hWorstMargin = TT('export.report.selectivity.table.headers.worst_margin', '最差裕度(s)');
    const hWorstAtI = TT('export.report.selectivity.table.headers.worst_at_i', '发生在 I(A)');
    const hPass = TT('export.report.selectivity.table.headers.pass', '是否满足');
    const hRecTms = TT('export.report.selectivity.table.headers.recommend_tms', '建议上级TMS');
    const hNote = TT('export.report.selectivity.table.headers.note', '说明');
    lines.push(`| ${hDown} | ${hUp} | ${hWorstMargin} | ${hWorstAtI} | ${hPass} | ${hRecTms} | ${hNote} |`);
    lines.push('|:---|:---|---:|---:|:---:|---:|:---|');
    matrix.pairs.forEach(p => {
      const worstMargin = p.worstMargin == null ? '—' : String(p.worstMargin);
      const worstAtI = p.worstAtI == null ? '—' : String(p.worstAtI);
      const okText = p.ok ? TT('export.common.yes', '是') : TT('export.common.no', '否');
      const recText = (p.recommendation && typeof p.recommendation.recommended === 'number')
        ? `${p.recommendation.recommended}${p.recommendation.capped ? TT('export.common.capped', '（受限）') : ''}`
        : '—';
      const note = `${TT('export.report.selectivity.note.min_margin_prefix', '最小裕度 ')}${p.minMargin}s`;
      lines.push(`| ${p.downstream} | ${p.upstream} | ${worstMargin} | ${worstAtI} | ${okText} | ${recText} | ${note} |`);
    });
    if (Array.isArray(matrix.adjustments) && matrix.adjustments.length > 0) {
      lines.push('');
      lines.push(TT('export.report.selectivity.adjustments.title', '### 顺序调整建议'));
      matrix.adjustments.forEach(adj => {
        const cappedText = adj.capped ? TT('export.common.capped', '（受限）') : '';
        const minMarginNote = `${TT('export.report.selectivity.note.min_margin_prefix', '最小裕度 ')}${adj.minMargin}s`;
        lines.push(`- 上级“${adj.label}” TMS：${adj.from} → ${adj.to}${cappedText}；${minMarginNote}`);
      });
    }
  }
  const safeTitle = title.replace(/[\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 40);
  const now = new Date();
  const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
  const filename = `${safeTitle || 'selectivity'}_${ts}.md`;
  return { md: lines.join('\n'), filename };
};

// 选择性矩阵导出（CSV）
export const buildSelectivityMatrixCsv = (matrix = {}, { minMargin = 0.3, t = null } = {}) => {
  const TT = (key, fallback) => (typeof t === 'function' ? (t(key) || fallback) : fallback);
  const header = [
    TT('export.report.selectivity.table.headers.downstream', 'downstream'),
    TT('export.report.selectivity.table.headers.upstream', 'upstream'),
    TT('export.report.selectivity.table.headers.worst_margin', 'worstMargin'),
    TT('export.report.selectivity.table.headers.worst_at_i', 'worstAtI'),
    TT('export.report.selectivity.table.headers.pass', 'ok'),
    TT('export.report.selectivity.table.headers.min_margin', 'minMargin'),
    TT('export.report.selectivity.table.headers.recommend_tms', 'recommended'),
    TT('export.report.selectivity.table.headers.capped', 'capped')
  ];
  const rows = [header.join(',')];
  if (Array.isArray(matrix?.pairs)) {
    matrix.pairs.forEach(p => {
      const recVal = (p.recommendation && typeof p.recommendation.recommended === 'number') ? p.recommendation.recommended : '';
      const capped = (p.recommendation && p.recommendation.capped) ? '1' : '0';
      rows.push([
        JSON.stringify(p.downstream ?? ''),
        JSON.stringify(p.upstream ?? ''),
        p.worstMargin ?? '',
        p.worstAtI ?? '',
        p.ok ? '1' : '0',
        p.minMargin ?? minMargin,
        recVal,
        capped
      ].join(','));
    });
  }
  const csv = rows.join('\n');
  const ts = `${new Date().getFullYear()}${String(new Date().getMonth()+1).padStart(2,'0')}${String(new Date().getDate()).padStart(2,'0')}_${String(new Date().getHours()).padStart(2,'0')}${String(new Date().getMinutes()).padStart(2,'0')}`;
  const filename = `selectivity_matrix_${ts}.csv`;
  return { csv, filename };
};

// 解析文件名前缀模板并清洗非法字符
// 支持占位符：{name}、{project}、{line}、{date}、{sort}
// 若未提供模板或模板为空，使用回退规则：按可用字段拼接 "project_line_date_sort"
export const resolveFilenamePrefix = (template, { name = '', project = '', line = '', sort = '' } = {}) => {
  const now = new Date();
  const dateStr = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
  const safe = (s) => String(s || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80);
  const ctx = { name: safe(name), project: safe(project), line: safe(line), date: safe(dateStr), sort: safe(sort) };
  const tpl = (typeof template === 'string' ? template.trim() : '');
  let out = '';
  if (tpl) {
    out = tpl
      .replace(/\{name\}/g, ctx.name)
      .replace(/\{project\}/g, ctx.project)
      .replace(/\{line\}/g, ctx.line)
      .replace(/\{date\}/g, ctx.date)
      .replace(/\{sort\}/g, ctx.sort)
      .trim();
  } else {
    // 回退拼接：忽略空字段，使用下划线连接
    out = [ctx.project, ctx.line, ctx.date, ctx.sort].filter(Boolean).join('_');
  }
  // 清理多余下划线与收尾
  out = out.replace(/_{2,}/g, '_').replace(/^_+|_+$/g, '');
  return out;
};

// 统一文件名片段规范化（非法字符剔除、空白转连字符、下划线转连字符、压缩多重连字符）
export const normalizeFilenameSegment = (s) => String(s || '')
  .trim()
  .replace(/[\\/:*?"<>|]+/g, '')
  .replace(/\s+/g, '-')
  .replace(/_+/g, '-')
  .replace(/-+/g, '-')
  .slice(0, 80);

const escapeRegex = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// 判断前缀中是否已包含名称（兼容连字符与下划线场景，统一按连字符匹配）
export const includesNameInPrefixNormalized = (prefix, name) => {
  const prefixNorm = normalizeFilenameSegment(prefix);
  const nameNorm = normalizeFilenameSegment(name);
  if (!prefixNorm || !nameNorm) return false;
  if (prefixNorm === nameNorm) return true;
  const re = new RegExp(`(^|-)${escapeRegex(nameNorm)}(-|$)`);
  return re.test(prefixNorm);
};

// 构建最终导出文件“基础名”（不含扩展名）：
// - 使用解析后的前缀与基础名进行去重合并
// - 统一使用连字符连接，去除非法字符，压缩多重连字符
// - 不附加时间戳，时间戳由调用方控制（目前关闭）
export const buildFinalBaseName = (baseName, prefixResolved) => {
  const nameNorm = normalizeFilenameSegment(baseName || '');
  const prefixNorm = normalizeFilenameSegment(prefixResolved || '');
  if (!prefixNorm) return nameNorm || 'unnamed';
  const final = includesNameInPrefixNormalized(prefixNorm, nameNorm)
    ? prefixNorm
    : `${prefixNorm}-${nameNorm}`;
  return final.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
};